import React, { useMemo } from "react";
import {
	Accordion as ChakraAccordion,
	AccordionItem,
	AccordionButton,
	AccordionPanel,
	AccordionIcon,
	Badge,
	Text,
} from "@chakra-ui/react";
import { FiDownload } from "react-icons/fi";
import type {
	EvaluationRevisionUpload,
	RevisionUploadType,
} from "../types/revision-review.type";
import { formatFileSize } from "../utils/format-file-size";

interface RevisionUploadAccordionProps {
	uploads: EvaluationRevisionUpload[];
}

const REVISION_TYPE_LABELS: Record<RevisionUploadType, string> = {
	quote: "Quote",
	questions: "Questions",
	submission_documents: "Submission Documents",
};

const REVISION_TYPE_COLORS: Record<RevisionUploadType, string> = {
	quote: "blue",
	questions: "purple",
	submission_documents: "green",
};

interface SupplierGroup {
	supplierName: string;
	uploads: EvaluationRevisionUpload[];
}

export const RevisionUploadAccordion: React.FC<RevisionUploadAccordionProps> = ({
	uploads,
}) => {
	const supplierGroups: SupplierGroup[] = useMemo(() => {
		const groupMap = new Map<number, SupplierGroup>();
		uploads.forEach((upload) => {
			const existing = groupMap.get(upload.rfx_invite_id);
			if (existing) {
				existing.uploads.push(upload);
			} else {
				groupMap.set(upload.rfx_invite_id, {
					supplierName: upload.supplier.name,
					uploads: [upload],
				});
			}
		});
		return Array.from(groupMap.values());
	}, [uploads]);

	if (supplierGroups.length === 0) {
		return (
			<div
				className="rounded-lg border border-dashed border-gray-300 p-8 text-center"
				data-testid="revision-uploads-empty"
			>
				<Text className="text-sm text-gray-400">
					No revision files have been uploaded
				</Text>
			</div>
		);
	}

	return (
		<ChakraAccordion allowMultiple data-testid="revision-upload-accordion">
			{supplierGroups.map((group) => (
				<AccordionItem
					key={group.supplierName}
					className="mb-2 rounded-lg border border-gray-200"
					border="none"
				>
					<AccordionButton className="rounded-lg px-4 py-3 hover:bg-gray-50">
						<div className="flex flex-1 items-center gap-2 text-left">
							<Text className="text-sm font-medium text-gray-800">
								{group.supplierName}
							</Text>
							<Badge
								colorScheme="gray"
								variant="subtle"
								className="text-xs"
							>
								{group.uploads.length}{" "}
								{group.uploads.length === 1 ? "revision" : "revisions"}
							</Badge>
						</div>
						<AccordionIcon />
					</AccordionButton>
					<AccordionPanel className="px-4 pb-4 pt-0">
						<div className="space-y-3">
							{group.uploads.map((upload) => (
								<div
									key={upload.id}
									className="rounded-md border border-gray-100 bg-gray-50 p-3"
									data-testid={`revision-upload-${upload.id}`}
								>
									<div className="mb-2 flex items-center gap-2">
										<Badge
											colorScheme={REVISION_TYPE_COLORS[upload.revision_type]}
											variant="subtle"
											className="text-xs"
										>
											{REVISION_TYPE_LABELS[upload.revision_type]}
										</Badge>
										<Text className="text-xs text-gray-400">
											{new Date(upload.uploaded_at).toLocaleDateString()}
										</Text>
										{upload.uploaded_by && (
											<Text className="text-xs text-gray-400">
												by {upload.uploaded_by.fullName}
											</Text>
										)}
									</div>
									{upload.comment && (
										<Text
											className="mb-2 text-sm text-gray-600"
											data-testid={`revision-upload-comment-${upload.id}`}
										>
											{upload.comment}
										</Text>
									)}
									{upload.files.length > 0 && (
										<div className="space-y-1">
											{upload.files.map((file) => (
												<a
													key={file.id}
													href={file.download_url}
													target="_blank"
													rel="noopener noreferrer"
													className="flex items-center gap-2 rounded px-2 py-1 text-sm text-blue-600 hover:bg-blue-50"
													data-testid={`revision-file-${file.id}`}
												>
													<FiDownload className="h-3.5 w-3.5 flex-shrink-0" />
													<span className="truncate">{file.file_name}</span>
													{file.file_size !== null && (
														<span className="ml-auto flex-shrink-0 text-xs text-gray-400">
															{formatFileSize(file.file_size)}
														</span>
													)}
												</a>
											))}
										</div>
									)}
								</div>
							))}
						</div>
					</AccordionPanel>
				</AccordionItem>
			))}
		</ChakraAccordion>
	);
};
